import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Handle token expiration (but not login errors)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login for 401 errors on authenticated routes (not login endpoint)
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/login")) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post("/auth/login", { username, password }),
  getProfile: () => api.get("/auth/profile"),
  // Login with Entra ID token (Sign in with Microsoft)
  loginWithToken: async (azureToken) => {
    try {
      const response = await api.post("/auth/login/token", { token: azureToken });

      if (response.data.code === 200) {
        const { token, userID } = response.data;
        
        // Store the token
        localStorage.setItem("token", token);
        
        // Fetch full user profile to get username, email, role, etc.
        try {
          const profileResponse = await api.get("/auth/profile");
          const userData = profileResponse.data;
          localStorage.setItem("user", JSON.stringify(userData));
          return { success: true, user: userData };
        } catch (profileError) {
          // If profile fetch fails, at least store the userID
          const minimalUser = { id: userID };
          localStorage.setItem("user", JSON.stringify(minimalUser));
          return { success: true, user: minimalUser };
        }
      }
      
      return {
        success: false,
        message: response.data.message || "Token login failed.",
      };
    } catch (error) {
      console.error("Token login error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Sign in with Microsoft failed. Please try again.",
      };
    }
  },
};

// Users API
export const usersAPI = {
  getAll: () => api.get("/users"),
  create: (userData) => api.post("/users", userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`),
  unlock: (id) => api.post(`/users/${id}/unlock`),
};

// Settings API
export const settingsAPI = {
  getRepositories: (configurationId) =>
    api.get(
      `/settings/repositories${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  createRepository: (data) => api.post("/settings/repositories", data),
  getDevOpsConnections: (configurationId) =>
    api.get(
      `/settings/devops${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  createDevOpsConnection: (data) => api.post("/settings/devops", data),
  syncTasks: (connectionId) =>
    api.post(`/settings/devops/${connectionId}/sync`),
  getAIConfig: (configurationId) =>
    api.get(
      `/settings/ai${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  createAIConfig: (data) => api.post("/settings/ai", data),
};

// Configurations API
export const configurationsAPI = {
  getAll: () => api.get("/configurations"),
  create: (data) => api.post("/configurations", data),
  update: (id, data) => api.put(`/configurations/${id}`, data),
  delete: (id) => api.delete(`/configurations/${id}`),
  getDetails: (id) => api.get(`/configurations/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getChangeRequests: (configurationId) =>
    api.get(
      `/dashboard/change-requests${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  createChangeRequest: (data) => api.post("/dashboard/change-requests", data),
  getChangeRequest: (id) => api.get(`/dashboard/change-requests/${id}`),
  regenerateChangeRequest: (id) =>
    api.post(`/dashboard/change-requests/${id}/regenerate`),
  deleteChangeRequest: (id) => api.delete(`/dashboard/change-requests/${id}`),
  getHistoricalEstimates: () => api.get("/dashboard/historical-estimates"),
  getAISubmissions: (configurationId) =>
    api.get(
      `/dashboard/ai-submissions${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  getWorkItems: (configurationId) =>
    api.get(
      `/dashboard/work-items${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  submitWorkItems: (data) => api.post("/dashboard/work-items/submit", data),
};

// Sprints API
export const sprintsAPI = {
  getAll: () => api.get("/sprints"),
  create: (data) => api.post("/sprints", data),
  update: (id, data) => api.put(`/sprints/${id}`, data),
  delete: (id) => api.delete(`/sprints/${id}`),
  getDetails: (id) => api.get(`/sprints/${id}`),
  allocateChangeRequest: (sprintId, changeRequestId) =>
    api.post(`/sprints/${sprintId}/allocate`, {
      sprint_id: sprintId,
      change_request_id: changeRequestId,
    }),
  removeAllocation: (sprintId, changeRequestId) =>
    api.delete(`/sprints/${sprintId}/allocate/${changeRequestId}`),
  getUnallocated: (configurationId) =>
    api.get(
      `/sprints/unallocated${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
};

// Feedback API
export const feedbackAPI = {
  submit: (data) => api.post("/feedback", data),
  getMyFeedback: () => api.get("/feedback/my-feedback"),
  getChannels: (configurationId) =>
    api.get(`/feedback/channels?configuration_id=${configurationId}`),
  createChannel: (data) => api.post("/feedback/channels", data),
  deleteChannel: (id) => api.delete(`/feedback/channels/${id}`),
  verifyChannel: (data) => api.post("/feedback/channels/verify", data),
};

// Workflow API
export const workflowAPI = {
  getBoard: (configurationId) =>
    api.get(
      `/workflow/board${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  getStatistics: (configurationId) =>
    api.get(
      `/workflow/statistics${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  updateStatus: (id, data) =>
    api.put(`/workflow/change-requests/${id}/status`, data),
  updateDetails: (id, data) =>
    api.put(`/workflow/change-requests/${id}/details`, data),
  getAudit: (id) => api.get(`/workflow/change-requests/${id}/audit`),
  getConfig: (configurationId) =>
    api.get(
      `/workflow/config${configurationId ? `?configuration_id=${configurationId}` : ""}`,
    ),
  updateConfig: (data) => api.put("/workflow/config", data),
  getUsersByRole: (role) =>
    api.get(`/workflow/users${role ? `?role=${role}` : ""}`),
};

export default api;
